import { format } from 'date-fns'
import { groq } from 'next-sanity'
import Image from 'next/image'
import { CardProps } from 'src/common/components/Card'
import { Lane } from 'src/common/components/Lane'
import { AppLayout } from 'src/common/components/Layout/AppLayout'
import { urlFor, usePreviewSubscription } from 'src/common/lib/sanity/sanity'
import { getClient } from 'src/common/lib/sanity/sanity.server'
import { wrapper } from 'src/store'

const latestPostsQuery = groq`
		*[_type == "post"] | order(publishedAt desc){
			author->{
				name,
				"image": image.asset,
				"slug": slug.current,
			},
			publishedAt,
			title,
			"slug": slug.current,
			mainImage,
			categories[]->{
				title,
				"slug": slug.current,
			},
			"plaintext": pt::text(body),
		}[0...15]
`

const BlogHomeQuery = groq`
*[_id == "singleton-bloghome"]{
	title,
	description,
	heroImage,
	categories[]->{
		title,
		"slug": slug.current,
		"relatedPosts": *[_type=='post' && references(^._id)]  | order(publishedAt desc){
			author->{
				name,
				"image": image.asset,
				"slug": slug.current,
			},
			publishedAt,
			title,
			"slug": slug.current,
			mainImage,
			categories[]->{
				title,
				"slug": slug.current,
			},
			"plaintext": pt::text(body),
		}[0...15]
	}
}  
`

const Blog = ({ blogHomeData, latestPosts }) => {
    const { data: blog } = usePreviewSubscription(BlogHomeQuery, {
        initialData: blogHomeData,
        // re-enable once pt::text bug is fixed in next-sanity lib
        enabled: false
    })

    const { heroImage, categories, title } = blog[0]

    return (
        <AppLayout pageTitle={title}>
            <div className="aspect-w-7 aspect-h-2 relative overflow-hidden mb-10">
                <Image
                    layout="fill"
                    src={heroImage.asset._ref}
                    loader={({ width, src, quality }) =>
                        urlFor(src).width(width).quality(quality).url()
                    }
                    placeholder="blur"
                    blurDataURL={urlFor(heroImage).blur(99).url()}
                    className="w-full h-auto"
                />
            </div>
            <div className="pl-2 md:pl-0">
                <Lane
                    title="Latest Posts"
                    data={
                        latestPosts?.map((item): CardProps => {
                            return {
                                cardType: 'blog',
                                sanitySrc: item.mainImage,
                                imageURL: '',
                                title: item.title,
                                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                                href: `/blog/${item.slug}`,
                                publishedDate: format(
                                    new Date(item.publishedAt),
                                    'MMM d'
                                ),
                                author: {
                                    imageURL: item?.author?.image
                                        ? urlFor(item.author.image)
                                              .width(38)
                                              .height(38)
                                              .url()
                                        : null
                                },
                                category: item?.categories?.length
                                    ? {
                                          color: '#8C80F8',
                                          name: item.categories[0]?.title,
                                          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                                          href: `/blog/categories/${item.categories[0]?.slug}`
                                      }
                                    : null,
                                previewText: item.plaintext
                            }
                        }) ?? []
                    }
                />
                {categories.map((category, idx) => {
                    return (
                        <Lane
                            key={idx}
                            title={category.title}
                            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                            seeAllHref={`/blog/categories/${category.slug}`}
                            data={
                                category.relatedPosts?.map((item): CardProps => {
                                    return {
                                        cardType: 'blog',
                                        sanitySrc: item.mainImage,
                                        imageURL: '',
                                        title: item.title,
                                        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                                        href: `/blog/${item.slug}`,
                                        publishedDate: format(
                                            new Date(item.publishedAt),
                                            'MMM d'
                                        ),
                                        author: {
                                            imageURL: item?.author?.image
                                                ? urlFor(item.author.image)
                                                      .width(38)
                                                      .height(38)
                                                      .url()
                                                : null
                                        },
                                        category: item?.categories?.length
                                            ? {
                                                  color: '#8C80F8',
                                                  name: item.categories[0]?.title,
                                                  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                                                  href: `/blog/categories/${item.categories[0]?.slug}`
                                              }
                                            : null,
                                        previewText: item.plaintext.slice(0, 180)
                                    }
                                }) ?? []
                            }
                        />
                    )
                })}
            </div>
        </AppLayout>
    )
}

export const getStaticProps = wrapper.getStaticProps(
    () =>
        async ({ preview = false }) => {
            const blogHomeData = await getClient(preview).fetch(BlogHomeQuery)
            const latestPosts = await getClient(preview).fetch(latestPostsQuery)

            return {
                props: { blogHomeData, latestPosts, preview },
                revalidate: 600
            }
        }
)

export default Blog
